import { Helmet } from 'react-helmet-async';
import ContactUsComponent from '../../components/ContactUsComponent';

const deliverables = [
  "Developed an intuitive frontend UI for cashiers to streamline the checkout process",
  "Implemented a robust backend system to manage inventories, employees, invoices, and user accounts",
  "Automated inventory tracking to ensure accurate stock levels",
  "Enabled employee management with role-based access control",
  "Integrated comprehensive invoicing features for easy transaction management",
  "Facilitated user management for seamless account creation and administration",
  "Built store management tools to oversee multiple locations and streamline operations",
  "Provided advanced reporting and analytics for business insights",
  "Ensured system scalability and performance optimization"
];

const POSSystem = () => {
  return (
    <>
      <Helmet>
        <title>Bell POS System - Smart Point of Sale Software by Data Repo</title>
        <meta
          name="description"
          content="Bell POS System by Data Repo is a smart point of sale software designed to manage billing, inventory, employees, invoices, and multi-location operations. A complete POS solution that helps businesses streamline sales and improve efficiency."
        />
      </Helmet>

      <section
        style={{
          backgroundImage: `url(../images/bg-casestudies-box.png)`,
          backgroundSize: "100% 80vh"
        }}
        className="hero--container relative flex flex-col justify-center w-full h-[60vh] bg-no-repeat bg-fixed md:bg-origin-border md:h-[80vh] pt-[65px]">
        <h1 className="text-center text-white text-2xl md:text-5xl font-bold uppercase">Bell POS System</h1>
        <div className="row--image-style bg-transparent w-full absolute bottom-0 left-0 right-0">
          <svg className="waves w-full h-44" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 300" preserveAspectRatio="none">
            <path fill="rgb(226 232 240)" d="M 1000 280 l 2 -253 c -155 -36 -310 135 -415 164 c -102.64 28.35 -149 -32 -235 -31 c -80 1 -142 53 -229 80 c -65.54 20.34 -101 15 -126 11.61 v 54.39 z"></path>
            <path fill="rgb(100 116 139)" d="M 1000 261 l 2 -222 c -157 -43 -312 144 -405 178 c -101.11 33.38 -159 -47 -242 -46 c -80 1 -153.09 54.07 -229 87 c -65.21 25.59 -104.07 16.72 -126 16.61 v 22.39 z"></path>
            <path fill="#E5E7EB" d="M 1000 300 l 1 -230.29 c -217 -12.71 -300.47 129.15 -404 156.29 c -103 27 -174 -30 -257 -29 c -80 1 -130.09 37.07 -214 70 c -61.23 24 -108 15.61 -126 10.61 v 22.39 z"></path>
          </svg>
        </div>
      </section>

      <section className="w-full pt-10 px-8 bg-gradient-to-r from-blue-100 via-white to-blue-100 relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-blue-200 rounded-full opacity-30 filter blur-3xl"></div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-purple-200 rounded-full opacity-20 filter blur-3xl"></div>

        <div className="flex flex-col md:flex-row items-center justify-center px-16 relative z-10">
          <div className="md:w-1/2 flex justify-center items-center p-5">
            <div className="bg-white rounded-xl shadow-2xl p-4 hover:scale-105 transform transition-transform duration-300">
              <img
                src="../images/bellpos-new.png"
                alt="car rental software dashboard"
                className="rounded-lg"
              />
            </div>
          </div>
          <div className="md:w-1/2 flex justify-center items-center p-5">
            <div className="bg-white rounded-xl shadow-2xl p-4 hover:scale-105 transform transition-transform duration-300">
              <img
                src="../images/bellpos-vendors.png"
                alt="car rental software dashboard"
                className="rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full items-center justify-center bg-gray-200">
        <div className="max-w-screen-lg mx-auto py-8">
          <div className="px-8 md:px-1 md:pr-2 py-6">
            <div className="mb-4 text-lg font-normal text-center md:text-left text-gray-600">
              <p>
                <strong>Industry:</strong> Retail; Inventory Management; Software
              </p>
              <p>
                <strong>Location:</strong> USA
              </p>
              <p>
                <strong>Areas of expertise:</strong> Back-end; Front-end; Quality assurance; Project management
              </p>
              <p>
                <strong>Timeline:</strong> 2017-now
              </p>
            </div>

            <p className="text-xl font-normal text-center md:text-left text-gray-600">
              Developed by Data Repo, our Bell POS System software is designed to streamline retail operations. The solution includes an intuitive frontend UI for cashiers, and a robust backend system to manage inventories, employees, invoices, user accounts, and store management.
            </p>

            <div className="pt-8">
              <h2 className="text-black text-2xl md:text-3xl font-bold py-5">
                Product Development and Backend Integration
              </h2>
              <p className="text-xl font-normal text-center md:text-left text-gray-600">
                Our Bell POS System simplifies the retail process by providing a user-friendly interface for cashiers and comprehensive backend management tools. The system automates inventory tracking, employee management, invoicing, and user administration, ensuring smooth and efficient store operations.
                <br /><br />
                The Data Repo team collaborated closely with the client to understand their needs and deliver a scalable, reliable, and user-friendly solution. We utilized advanced technologies to enhance functionality and optimize performance.
              </p>
            </div>

            <div className="pt-8">
              <h2 className="text-black text-2xl md:text-3xl font-bold py-5">
                Key Features
              </h2>
              <p className="text-xl font-normal text-center md:text-left text-gray-600 pb-8">
                Bell POS System transforms everyday retail operations into a seamless, data-driven experience.
                Designed for growing businesses that demand precision, performance, and profitability — it integrates inventory, staff, billing, and reporting into one intelligent platform.
                <br /><br />
                Explore below how each feature helps you simplify management, elevate customer experiences, and maximize your business potential.
              </p>

              <div className="space-y-8 text-xl font-normal text-gray-600">
                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    1. Smart Inventory Management
                  </h3>
                  <p>
                    Take complete control of your stock with Bell POS System.
                    Easily track and manage your inventory, update quantities, and record item details across one or multiple locations.
                    <br /><br />
                    With built-in barcode scanning and clear visibility into stock levels, you can maintain accuracy, prevent shortages, and keep your operations running smoothly.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    2. Department and Category Management
                  </h3>
                  <p>
                    Structure your business for success. Bell POS System lets you organize products by departments, brands, or categories, creating a clear hierarchy that simplifies navigation and analytics.
                    <br /><br />
                    Gain actionable insights into which product lines perform best, identify slow movers, and fine-tune strategies that drive profitability. Every item, every sale, perfectly organized and optimized.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    3. Vendor and Supplier Management
                  </h3>
                  <p>
                    Build stronger partnerships with suppliers through complete visibility and communication.
                    Bell POS System stores detailed vendor profiles, manages purchase histories, so your supply chain stays smooth and reliable.
                    <br /><br />
                    Generate purchase orders directly from vendor records and maintain clear payment tracking — eliminating paperwork while ensuring every product arrives right on time.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    4. Purchase Orders and Invoice Management
                  </h3>
                  <p>
                    Simplify your purchasing and billing process with Bell POS System.
                    Easily create, approve, and manage purchase orders and invoices for both vendors and customers — all in one place.
                    <br /><br />
                    Keep your financial records organized and accessible, with every transaction properly documented and every invoice looking professional.
                    Bell POS helps your business stay efficient, compliant, and ready for growth.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    5. Deals and Promotions Management
                  </h3>
                  <p>
                    Turn everyday customers into loyal fans with Bell POS System.
                    Easily create attractive discounts, special offers, and loyalty programs that keep your customers coming back for more.
                    <br /><br />
                    Manage pricing directly at checkout, apply promotional campaigns effortlessly, and track which deals bring in the best results — all from one convenient platform.
                    Bell POS helps you boost sales, increase engagement, and grow your customer base with ease.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    6. Employee and User Management
                  </h3>
                  <p>
                    Empower your workforce with clarity and control.
                    Bell POS System enables role-based access, detailed activity tracking, and attendance management to streamline operations and accountability.
                    <br /><br />
                    From cashiers to managers, every team member gets the right tools and permissions to perform efficiently — while you maintain total visibility across your workforce.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    7. Store and Multi-Location Management
                  </h3>
                  <p>
                    Expand your business with confidence. Bell POS System unifies all your stores under one dashboard — giving you real-time visibility of sales, inventory, and performance across every branch.
                    <br /><br />
                    Compare results, balance stock, and maintain consistent pricing and service quality — ensuring every location runs as efficiently as your flagship store.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-8">
              <h2 className="text-black text-2xl md:text-3xl font-bold py-5">
                Deliverables
              </h2>
              <ul className="list-disc pl-5 text-xl font-normal text-center md:text-left text-gray-600">
                {deliverables.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="pt-8">
              <h2 className="text-black text-2xl md:text-3xl font-bold py-5">
                Project Results
              </h2>
              <p className="text-xl font-normal text-center md:text-left text-gray-600 pb-8">
                The Bell POS System developed by Data Repo has significantly improved the efficiency and effectiveness of retail operations. The system's scalability and performance optimization have enabled the client to handle an increasing number of transactions with ease.
              </p>
              <ul className="list-disc pl-5 text-xl font-normal text-center md:text-left text-gray-600">
                <li>Enhanced system stability and performance.</li>
                <li>Streamlined operations, reducing time and cost.</li>
                <li>Achieved significant business growth and increased revenue.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <ContactUsComponent />
    </>
  );
}

export default POSSystem;